import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../services/utils';

import { useLocale } from './useLocale';
import { SUPPORTED_LOCALES, type SupportedLocale } from './types';

interface LanguageSwitcherProps {
  className?: string;
}

/**
 * Language toggle. Shows a globe icon + the active locale's short code
 * (e.g. "EN" / "VI") so users can see the current language at a glance.
 * Persists the choice via i18next LanguageDetector (localStorage) and
 * triggers re-render of every consumer of useTranslation in the app.
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { locale, setLocale } = useLocale();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('gap-1.5 px-2', className)}
          aria-label={t('language.label')}
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          <span className="text-xs font-medium">
            {t(`language.${locale}Short`)}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => setLocale(code as SupportedLocale)}
            className={cn(
              'cursor-pointer',
              locale === code && 'font-semibold',
            )}
          >
            {t(`language.${code}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
